import { useEffect } from "react"
import { useLocation } from "react-router"
import { trackUserCapturedByAd, type AdUtmParams } from "./analyticsEvents"

/**
 * Detects ad-referred visits and fires the "User Captured By Ad" analytics
 * event, mirroring backflip-mobile's `userCapturedByAd` trigger.
 *
 * Ads land visitors on entry URLs like `/loans/setup?utm_source=…&utm_campaign=…`.
 * We read the UTM params off the URL on every navigation and fire the event the
 * first time we see a given UTM set. Detection lives at the app root so it works
 * no matter which route the ad points at (`/loans/setup`, `/auth/register`, a
 * public page, etc.) and fires BEFORE the auth gate redirects an unauthenticated
 * visitor (which drops the query string).
 */

/** Maps the snake_case query-param names ads use to our camelCase keys. */
const UTM_URL_KEYS: ReadonlyArray<readonly [keyof AdUtmParams, string]> = [
  ["utmSource", "utm_source"],
  ["utmMedium", "utm_medium"],
  ["utmCampaign", "utm_campaign"],
  ["utmContent", "utm_content"],
  ["utmTerm", "utm_term"],
]

/**
 * Extract UTM params from a raw search string (e.g. `?utm_source=fb`). Returns
 * `null` when the URL carries no UTM params.
 */
export function parseUtmFromSearch(search: string): AdUtmParams | null {
  const params = new URLSearchParams(search)
  const utm: AdUtmParams = {}
  let found = false
  for (const [key, urlKey] of UTM_URL_KEYS) {
    const value = params.get(urlKey)
    if (value !== null && value !== "") {
      utm[key] = value
      found = true
    }
  }
  return found ? utm : null
}

/** Stable signature for a UTM set, used to fire the event once per set. */
function utmSignature(utm: AdUtmParams): string {
  return JSON.stringify(UTM_URL_KEYS.map(([key]) => utm[key] ?? null))
}

const AD_CAPTURE_SESSION_KEY = "bf_ad_capture_signature"

/**
 * Where the captured UTM set is kept so registration can send it to the API
 * later. `localStorage`, not `sessionStorage`: an ad click and the eventual
 * sign-up are frequently different visits (click an ad, look around, come back
 * and register), and backflip-mobile persists its own UTM store across app
 * restarts for the same reason. Attribution that only survives one tab session
 * would miss most real sign-ups.
 */
const UTM_STORAGE_KEY = "bf_utm"

/**
 * The stored shape: same keys as `AdUtmParams` but values are always present
 * strings when set. `AdUtmParams` permits `null` (the analytics event tolerates
 * it); the registration API does not, so reads are narrowed to this.
 */
export type UtmValues = Partial<Record<keyof AdUtmParams, string>>

/**
 * Persist the UTM set for registration. Last write wins, matching mobile's
 * `updateUtm`. Zion writes these to HubSpot as create-only contact properties,
 * so whatever is stored at account creation is permanent — no later correction.
 */
function storeUtm(utm: AdUtmParams): void {
  try {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm))
  } catch {
    // Private mode / full storage: attribution is lost, which must never break
    // the visit. The analytics event below still fires.
  }
}

/**
 * The UTM set captured on this or an earlier visit, or `null` when the visitor
 * never arrived from an ad. Read at registration time.
 */
export function getStoredUtm(): UtmValues | null {
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY)
    if (raw === null) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null) return null
    // Only pass through the keys we own — a hand-edited or stale value must not
    // become arbitrary fields on an API request.
    const utm: UtmValues = {}
    for (const [key] of UTM_URL_KEYS) {
      const value = (parsed as Record<string, unknown>)[key]
      if (typeof value === "string" && value !== "") utm[key] = value
    }
    return Object.keys(utm).length > 0 ? utm : null
  } catch {
    return null
  }
}

/**
 * Drop the stored UTM once it has been recorded against an account. Prevents a
 * second sign-up in the same browser from inheriting the first one's
 * attribution — mobile clears its store on logout for the same reason.
 */
export function clearStoredUtm(): void {
  try {
    localStorage.removeItem(UTM_STORAGE_KEY)
  } catch {
    // Nothing to do — a failed clear only risks stale attribution.
  }
}

/**
 * Session-scoped de-dupe: a UTM set fires once per tab session (survives
 * internal navigations and reloads; a fresh session re-fires, matching mobile's
 * per-open semantics). A different UTM set has a new signature and fires again.
 */
function alreadyCaptured(signature: string): boolean {
  try {
    return sessionStorage.getItem(AD_CAPTURE_SESSION_KEY) === signature
  } catch {
    return false
  }
}

function markCaptured(signature: string): void {
  try {
    sessionStorage.setItem(AD_CAPTURE_SESSION_KEY, signature)
  } catch {
    // Ignore disabled/full storage — worst case we re-fire the event.
  }
}

/**
 * App-wide ad-capture detection. Mount once at the app root. Fires
 * "User Captured By Ad" the first time an ad-referred URL is seen in a session.
 */
export function useCaptureAdEvent(): void {
  const location = useLocation()
  useEffect(() => {
    const utm = parseUtmFromSearch(location.search)
    if (!utm) return

    // Persist on every sighting, even a repeat within the session: the event is
    // de-duped for analytics, but the stored value must reflect the latest ad
    // regardless of whether we already reported this one.
    storeUtm(utm)

    const signature = utmSignature(utm)
    if (alreadyCaptured(signature)) return

    markCaptured(signature)
    trackUserCapturedByAd(utm)
  }, [location.search])
}
